/**
 * Buscar y elegir un producto del catálogo para un combo (spec 20): agregar un componente, cambiar el de una fila o
 * sumar un empaque alternativo. Solo productos activos; un servicio o un producto que ya está en el combo no se eligen.
 */
import type { ProductoResumenDTO } from '@mg/shared';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { PaginadorBusqueda, useMismaBusqueda, usePaginaBusqueda } from '@/componentes/PaginadorBusqueda.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input } from '@/componentes/ui/input.tsx';
import { useProductos } from '@/modulos/productos/api.ts';

/** Productos por página en el buscador. */
const LIMITE = 20;

interface Props {
  abierto: boolean;
  titulo: string;
  descripcion: string;
  cerrar: () => void;
  alElegir: (p: ProductoResumenDTO) => void;
  /** Los que ya están en el combo (salvo el de la fila que se cambia). */
  excluir: Set<string>;
}

export function DialogElegirProducto({ abierto, titulo, descripcion, cerrar, alElegir, excluir }: Props) {
  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-w-2xl" data-testid="dialogo-elegir-producto">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription>{descripcion}</DialogDescription>
        </DialogHeader>
        {abierto ? <Buscador alElegir={alElegir} excluir={excluir} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function Buscador({ alElegir, excluir }: Pick<Props, 'alElegir' | 'excluir'>) {
  const [q, setQ] = useState('');
  const [pagina, setPagina] = usePaginaBusqueda(q);
  const productos = useProductos({ q: q.trim() || undefined, activo: 'true', pagina, limite: LIMITE });
  const misma = useMismaBusqueda(q, !!productos.data && !productos.isPlaceholderData);
  const lista = misma ? (productos.data?.datos ?? []) : [];
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input autoFocus placeholder="Buscar por código, nombre o marca…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" aria-label="Buscar producto" data-testid="combo-producto-buscar" />
      </div>
      <div className="max-h-[55vh] overflow-y-auto rounded-lg border">
        {productos.isLoading || !misma ? <p className="p-3 text-sm text-muted-foreground">Buscando…</p> : null}
        {misma && productos.data && lista.length === 0 ? <p className="p-3 text-sm text-muted-foreground">Ningún producto activo coincide{q ? ` con «${q}»` : ''}.</p> : null}
        {lista.map((p) => {
          const servicio = p.tipo === 'servicio';
          const yaEsta = excluir.has(p.id);
          const sinPresentacion = !p.presentaciones.some((x) => x.activa);
          const bloqueado = servicio || yaEsta || sinPresentacion;
          return (
            <button
              key={p.id}
              type="button"
              disabled={bloqueado}
              onClick={() => alElegir(p)}
              className="flex w-full items-center justify-between gap-2 border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
              data-testid={`combo-producto-opcion-${p.codigo}`}
            >
              <span className="min-w-0">
                <span className="font-medium">{p.nombre}</span> <span className="font-mono text-xs text-muted-foreground">{p.codigo}</span>
                <span className="block text-[11px] text-muted-foreground">{[p.marca, p.color, p.unidad].filter(Boolean).join(' · ')}</span>
              </span>
              {servicio ? <Badge variant="neutro">Servicio: no va en un combo</Badge> : yaEsta ? <Badge variant="neutro">Ya está en el combo</Badge> : sinPresentacion ? <Badge variant="pendiente">Sin presentación activa</Badge> : null}
            </button>
          );
        })}
        <PaginadorBusqueda pagina={pagina} limite={LIMITE} total={misma ? (productos.data?.total ?? 0) : 0} onPagina={setPagina} testid="combo-producto" />
      </div>
    </div>
  );
}
